import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from './useApi';
import { queryClient } from './react-query';

export interface AccountStats {
  _id: string;
  name: string;
  contract_expiration_date?: string;
  last_login?: string;
  trial?: boolean;
  extensions?: {
    [key: string]: string[];
  };
  agreements?: {
    active?: {
      agreements: number;
      journals: number;
    };
    expired?: {
      agreements: number;
      journals: number;
    };
    pending?: {
      agreements: number;
      journals: number;
    };
  };
}

type UpdateAccountArgs = {
  path: string;
  data: Partial<AccountStats>;
};

type CreateAccountArgs = {
  name: string;
  path: string;
  contract_expiration_date?: string;
  trial: boolean;
};

export const useAccountStats = () => {
  const { makeApiRequest } = useApi();

  const { data, isLoading, error, refetch } = useQuery<AccountStats[]>({
    queryKey: ['accountStats'],
    queryFn: () => makeApiRequest('/accounts/stats', 'GET'),
  });

  const { mutateAsync: updateAccountMutation } = useMutation({
    mutationFn: async ({ path, data }: UpdateAccountArgs) => {
      await makeApiRequest(`/accounts/${path}`, 'PUT', data);
    },
    onSuccess: () => {
      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['accountStats'] });
        queryClient.invalidateQueries({ queryKey: ['account'] });
      }, 500);
    },
  });

  const { mutateAsync: disableAccountMutation } = useMutation({
    mutationFn: async (path: string) => {
      await makeApiRequest(`/accounts/${path}`, 'DELETE');
    },
    onSuccess: () => {
      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['accountStats'] });
        queryClient.invalidateQueries({ queryKey: ['account'] });
      }, 500);
    },
  });

  const { mutateAsync: createAccountMutation } = useMutation({
    mutationFn: async (data: CreateAccountArgs) => {
      await makeApiRequest(`/accounts/${data.path}`, 'PUT', data);
    },
    onSuccess: () => {
      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['accountStats'] });
        queryClient.invalidateQueries({ queryKey: ['account'] });
      }, 500);
    },
  });

  return {
    accountStats: data || [],
    isLoading,
    error,
    refetch,
    createAccount: createAccountMutation,
    updateAccount: updateAccountMutation,
    disableAccount: disableAccountMutation,
  };
};